import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  NgSelectComponent,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
} from '@ng-select/ng-select';
import { AccordionComponent } from '../../../../../shared/components/accordion/accordion.component';
import { AccordionItemComponent } from '../../../../../shared/components/accordion/accordion-item.component';
import { AlertComponent } from '../../../../../shared/components/alert/alert.component';
import { LoadingComponent } from '../../../../../shared/components/loading/loading.component';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { SimulationService } from '../../../../../core/services/simulation/simulation.service';
import { FranchiseResultModel } from '../../../../../core/services/inventory/inventory.service';
import { TradePolicyModel } from '../../../../../core/models/catalog/trade-policy.model';
import { customFranchiseSearchFn } from '../../../../../core/helpers/franchise-search.helper';
import { getHttpErrorMessage } from '../../../../../core/helpers/http-error-response.helper';
import {
  getSimulationGenericMessage,
  VtexSimulationShippingResponseFullModel,
} from '../../../../../core/models/simulation/simulate-sku-freight.model';

@Component({
  selector: 'app-sku-simulation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    AccordionComponent,
    AccordionItemComponent,
    AlertComponent,
    LoadingComponent,
    ButtonComponent,
  ],
  templateUrl: './sku-simulation.component.html',
})
export class SkuSimulationComponent {
  @Input({ required: true }) skuId!: number;
  @Input({ required: true }) franchises!: FranchiseResultModel[];
  @Input({ required: true }) tradePolicies!: TradePolicyModel[];
  @Input() loadingAccounts = false;

  simulationAccounts: string[] | string = [];
  simulationSalesChannel: string = '';
  loading = false;
  error: string | null = null;
  postalCode: string = '';
  result: Map<string, VtexSimulationShippingResponseFullModel> = new Map();

  customSearchFn = customFranchiseSearchFn;
  protected readonly getSimulationGenericMessage = getSimulationGenericMessage;

  constructor(private readonly simulation: SimulationService) {}

  private validatePostalCode(postalCode: string): boolean {
    if (!postalCode) return false;
    const cleanPostalCode = postalCode.replace(/\D/g, '');
    return /^\d{8}$/.test(cleanPostalCode);
  }

  simulate(): void {
    this.error = null;
    this.loading = true;

    let selectedAccounts: string[];
    if (this.simulationAccounts instanceof Array) {
      selectedAccounts = this.simulationAccounts;
    } else {
      selectedAccounts = [this.simulationAccounts];
    }

    if (!this.validatePostalCode(this.postalCode)) {
      this.error = 'CEP inválido. Use o formato 00000-000 ou 00000000';
      this.loading = false;
      return;
    }

    this.result = new Map();

    const accounts = selectedAccounts.filter(a => a !== 'ALL');
    this.simulation
      .simulateSku({
        accounts,
        zipCode: this.postalCode.replace(/\D/g, ''),
        salesChannel: Number(this.simulationSalesChannel) || 1,
        items: [{ idSku: this.skuId.toString(), price: 0, quantidade: 1 }],
      })
      .subscribe({
        next: res => {
          if (res instanceof Map) {
            this.result = res;
          } else {
            this.result = new Map(Object.entries(res as any));
          }
          this.loading = false;
        },
        error: err => {
          console.error(err);
          this.loading = false;
          this.error = getHttpErrorMessage(err);
        },
      });
  }

  getSimulationLabel(
    accountName: string,
    model: VtexSimulationShippingResponseFullModel
  ): string {
    if (model.freightSimulated?.some(x => x.slaResponseFreightSimulated?.length)) {
      let count = 0;
      for (const dock of model.freightSimulated) {
        count += dock?.slaResponseFreightSimulated?.length ?? 0;
      }
      return `${accountName} - Entregável (${count} docas disponíveis)`;
    }
    return `${accountName} - Não possui entrega disponível`;
  }

  hasDeliveryAvailable(model: VtexSimulationShippingResponseFullModel): boolean {
    return !!model.freightSimulated?.some(x => x.slaResponseFreightSimulated?.length);
  }

  private getTotalStock(model: VtexSimulationShippingResponseFullModel): number {
    let total = 0;
    for (const dock of model.freightSimulated ?? []) {
      for (const sla of dock.slaResponseFreightSimulated ?? []) {
        total += sla.availabilityQuantity ?? 0;
      }
    }
    return total;
  }

  get sortedResults(): { key: string; value: VtexSimulationShippingResponseFullModel }[] {
    return [...this.result.entries()]
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => this.getTotalStock(b.value) - this.getTotalStock(a.value));
  }
}
